import { useTranslate } from '../../application/i18n/LocaleProvider';

/**
 * The device's best runs, shown once a run has ended (SPEC-007 PR-7.7).
 *
 * ON THIS DEVICE, and the copy says so on the label rather than in a footnote.
 * A "leaderboard" normally means other people; this one cannot, because Legends
 * has no backend and is not getting one (ADR-0004). Calling it what it is costs
 * three words and is the difference between a feature and a small lie.
 *
 * Drawn as one row of scores rather than as a table of places, because on a
 * phone it has about thirty pixels to live in — the screen does not scroll, and
 * every pixel here comes out of the explanation above it. The run that just
 * finished is marked in the ramp, which is the only thing a reader is actually
 * looking for. On a wide screen the row becomes a column in the right-hand rail
 * (`duelBoard.css`), which is a stylesheet's decision and not this file's.
 *
 * IT IS ON SCREEN FOR THE WHOLE RUN NOW, not only at an ending. It used to be
 * rendered inside `DuelReveal`, which is where it belonged while the two were
 * always drawn together; given a rail it is what the reader is playing AGAINST,
 * and a target you can only see once you have missed it is not a target.
 *
 * WHICH IS WHY THE STANDING SENTENCE IS GATED ON THE SCORE. That line — "number
 * three on this device", "not a top five run this time" — is about a run that
 * has FINISHED, and told to somebody four rounds into a live one it is simply
 * false. `DuelScreen` passes a zero while the run is open, which is the same
 * signal that already suppressed the final-score clause beside it.
 *
 * It carries its own `role="status"` for the same move: the sentence used to be
 * inside the reveal's live region and is now a sibling of it, so what a screen
 * reader is told when a run lands on the board has to travel with the sentence.
 */
export default function DuelBoard({ board, rank, score }) {
    const t = useTranslate();

    if (board.length === 0) {
        return null;
    }

    return (
        <div className="duel-board">
            <p className="duel-board__title">{t('duel.board.title')}</p>

            <ol className="duel-board__rows">
                {board.map((row, place) => (
                    <li
                        className={`duel-board__row${place + 1 === rank ? ' duel-board__row--mine' : ''}`}
                        // Place, not score: two runs tie at the bottom of a
                        // five-row board often enough that a score is not a key.
                        key={place}
                    >
                        <span className="figure">{row.score}</span>
                    </li>
                ))}
            </ol>

            {/* What just happened, said in words as well as in colour — a mark
                on a row is invisible to anybody reading by ear. See the note
                above for why a live run draws none of it. */}
            {score > 0 ? (
                <p className="duel-board__standing" role="status">
                    {rank ? t('duel.board.placed', { rank }) : t('duel.board.none', { total: board.length })}
                    {` ${t('duel.finalScore', { count: score })}`}
                </p>
            ) : null}
        </div>
    );
}
